import theme, { colors, size, fontSize, appTopbar, appBottombar } from '@styles/theme'
import useCurrentLocationContext from '@contexts/currentLocationContext'
import useMapDataContext, { MapView } from '@contexts/mapDataContext'
import useScrollTriggerContext from '@contexts/scrollTriggerContext'
import useNearbyFilterContext from '@contexts/nearbyFilterContext'
import useTravelModeContext from '@contexts/travelModeContext'
import { useLocation, useNavigate } from 'react-router-dom'
import useDrawerContext from '@contexts/drawerContext'
import { useAuthContext } from '@contexts/authContext'
import useCableContext from '@contexts/cableContext'
import useModalContext from '@contexts/modalContext'
import useYesOrNo from '@hooks/useYesOrNo'
import useDialog from '@hooks/useDialog'
import styled from '@emotion/styled'

import NearbyScreenDrawerContents from '@components/layout/right_drawers/nearby_screen/NearbyScreenDrawerContents'
import { Toolbar, Button, Select, MenuItem, FormControl, IconButton } from '@mui/material'
import NearbyFilterDialog from '@components/nearby/nearby_filters/NearbyFilterDialog'
import TravelModeTargetIndicator from './travel_mode/TravelModeTargetIndicator'
import TravelModeSelectorButton from './travel_mode/TravelModeSelectorButton'
import NearbyStatusDialog from '@components/nearby/NearbyStatusDialog'
import { TbAdjustmentsHorizontal, TbMenu2 } from 'react-icons/tb'
import { NearbyProfilesLoading } from './NearbyProfilesLoading'
import AppBarAvatar from '@elements/AppBarAvatar'
import BaseAppBar from '@elements/BaseAppBar'
import HiddenYesOrNo from './HiddenYesOrNo'
import { Emoji } from 'emoji-picker-react'
import {
  PiCaretRightBold,
  PiCrosshair,
  PiEyeSlash,
} from 'react-icons/pi'
import { PostActionBtn } from '@elements/FloatingActionBtn'
// import FloatActionBtn from '@elements/FloatingActionBtn'

const AppBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  z-index: 100;
  height: ${appTopbar};
  width: 100%;
  
  border-bottom: 0.5px solid rgba(255,255,255,0.2);
  ${theme.glassBackgruond(0.9)}
`

const NearbyToolbar = styled(Toolbar)`
  width: 100vw;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${appTopbar} !important;
`

const FloatingButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  flex-shrink: 0;
  height: ${size(5)};
  padding: 0 ${size(2)};
  border: 0.5px solid rgba(255,255,255, 0.15);
  color: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.25);
  backdrop-filter: blur(3px);
  line-height: ${fontSize('md')};
  font-size: ${fontSize('sm')};
  font-weight: 500;
  background-color: rgba(20,20,20,0.9);

  &:hover {
    background-color: rgba(20,20,20,0.9);
  }

  &:focus {
    background-color: rgba(20,20,20,0.9);
  }
`

const StatusEmoji = styled(Emoji)``

const Up1 = styled.span`
  display: flex;
  align-items: center;
  line-height: 0;
  transform: translateY(-1px);
`

const Up2 = styled.span`
  display: flex;
  align-items: center;
  line-height: 0;
  transform: translateY(-2px);
`

const Down1 = styled.span`
  display: flex;
  align-items: center;
  line-height: 0;
  transform: translateY(1px);
`

const StatusText = styled.span`
  margin-left: ${size(0.75)};
`

const OptionWrapper = styled.div`
  display: flex !important;
  align-items: center !important;
  padding-right: 0;
  font-weight: 500;
`

const StyledSelect = ({ wrapperStyles, children, ...rest }) => {
  return (
    <Select
      sx={{
        '.MuiSelect-select': {
          paddingLeft: `16px !important`,
          paddingRight: `16px !important`,
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
        },
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '.MuiSvgIcon-root ': {
          display: 'none',
        },
        borderRadius: '100px',
        backgroundColor: 'rgba(20,20,20,0.9)',
        color: 'white',
        border: '0.5px solid rgba(255,255,255, 0.15)',
        height: size(4),
        fontSize: fontSize('sm'),
        ...wrapperStyles,
      }}
      {...rest}>
      {children}
    </Select>
  )
}

const NearbyTopNav = () => {
  // Contexts
  const { map, profilesInitialized, isProfilesReloading } = useMapDataContext()
  const { state: authState, showHiddenDisplay } = useAuthContext()
  const { isTravelModeSelectorEnabled } = useTravelModeContext()
  const { currentLocation } = useCurrentLocationContext()
  const { shouldReauthWebsocket, isReconnecting } = useCableContext()
  const { scrollTrigger } = useScrollTriggerContext()
  const { savedState } = useNearbyFilterContext()
  const { openRightDrawer } = useDrawerContext()
  const { newPost } = useModalContext()
  const location = useLocation()
  const navigate = useNavigate()

  // State and Hooks
  const [isFilterDialogOpen, openFilterDialog, closeFilterDialog] = useDialog('nearby_filters', 'nearby-filters-open')
  const [isStatusDialogOpen, openStatusDialog, closeStatusDialog] = useDialog('nearby_status', 'nearby-status-open')
  const [isHideDialogOpen, openHideDialog, closeHideDialog] = useYesOrNo('hide-yourself-confirmation')
  // TODO: We'll have to update traveling state to what gets set on the backend... I think.
  const isGrid = location.pathname === '/grid'

  const navigateToUserOnMap = () => {
    if (map && currentLocation && authState?.user?.nearbyUuid) {
      MapView.focusOnUser(authState?.user)
      map.easeTo({center: currentLocation, zoom: 14, duration: 2000});
    }
  }

  const openNearbySettings = () => {
    openRightDrawer(<NearbyScreenDrawerContents />)
  }

  return (
    <div style={{ height: '100dvh', width: '100dvw', position: 'relative' }}>
      <NearbyProfilesLoading
        profilesInitialized={profilesInitialized}
        isProfilesReloading={isProfilesReloading}
        shouldReauthWebsocket={shouldReauthWebsocket}
        isReconnecting={isReconnecting}
      />
      <TravelModeTargetIndicator />
      <HiddenYesOrNo
        isOpen={isHideDialogOpen}
        setClosed={closeHideDialog}
      />
      <NearbyFilterDialog
        isOpen={isFilterDialogOpen}
        setClosed={closeFilterDialog}
        afterSelect={() => console.log('something')}
      />
      <NearbyStatusDialog
        isOpen={isStatusDialogOpen}
        setClosed={closeStatusDialog}
      />
      <AppBarContainer className={scrollTrigger ? 'animation__hide-top-nav' : 'animation__show-top-nav'}>
        <BaseAppBar sx={{ boxShadow: 'none' }}>
          <NearbyToolbar>
            <AppBarAvatar />
            <TravelModeSelectorButton />
            <IconButton
              disabled={!profilesInitialized}
              onClick={openNearbySettings}
              style={{ padding: 0 }}>
              <TbMenu2 size={size(3)} color='white' />
            </IconButton>
          </NearbyToolbar>
        </BaseAppBar>
      </AppBarContainer>
      {/* TODO: This has some serious issues with being responsive because material-ui is changing the size of the appbar with the size of the page */}
      {profilesInitialized &&
        <div
          className={scrollTrigger ? 'animation__hide-top-nav' : 'animation__show-top-nav'}
          style={{ position: 'absolute', top: appTopbar, width: '100%', padding: '6px 8px 0 8px', display: 'flex', justifyContent: 'space-between', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FormControl size='small'>
              <StyledSelect
                disabled={showHiddenDisplay}
                label='Looking Status'
                size='small'
                onClick={() => !showHiddenDisplay && openStatusDialog()}
                open={false}
                wrapperStyles={{
                  backgroundColor: showHiddenDisplay ? '#333333' : 'rgba(20,20,20,0.9)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                  backdropFilter: 'blur(3px)',
                  fontWeight: '500',
                  height: size(5)
                }}
                value={authState?.user?.tempStatus || 'browsing'}>
                <MenuItem value='browsing'>
                  <OptionWrapper>
                    <Up1>
                      <StatusEmoji unified="1f60e" size="18" />
                    </Up1>
                    <StatusText>
                      Browsing
                    </StatusText>
                  </OptionWrapper>
                </MenuItem>
                <MenuItem value='right_now'>
                  <OptionWrapper>
                    <StatusEmoji unified="1f440" size="18" />
                    <StatusText>
                      Right Now
                    </StatusText>
                  </OptionWrapper>
                </MenuItem>
                <MenuItem value='meetup'>
                  <OptionWrapper>
                    <Up2>
                      <StatusEmoji unified="1f91d" size="18" />
                    </Up2>
                    <StatusText>
                      Meet Up
                    </StatusText>
                  </OptionWrapper>
                </MenuItem>
                <MenuItem value='chatting'>
                  <OptionWrapper>
                    <StatusEmoji unified="1f4ac" size="18" />
                    <StatusText>
                      Chatting
                    </StatusText>
                  </OptionWrapper>
                </MenuItem>
                <MenuItem value='romance'>
                  <OptionWrapper>
                    <Up1>
                      <StatusEmoji unified="1f496" size="18" />
                    </Up1>
                    <StatusText>
                      Romance
                    </StatusText>
                  </OptionWrapper>
                </MenuItem>
              </StyledSelect>
            </FormControl>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FloatingButton
              disabled={authState?.user?.isAnonymous && isTravelModeSelectorEnabled}
              onClick={openHideDialog}
              style={{
                marginRight: size(1),
                minWidth: size(5),
                height: size(5),
                padding: 0,
                backgroundColor: authState?.user?.isHidden && colors.statusColors.right_now('primary')
              }}>
              <PiEyeSlash size={size(2.75)} />
            </FloatingButton>
            <FloatingButton
              disabled={showHiddenDisplay}
              style={{ backgroundColor: showHiddenDisplay && '#333333', width: size(10.25) }}
              onClick={() => navigate(`/${isGrid ? 'map' : 'grid'}`)}>
              {isGrid ? 'Map' : <Down1>Grid</Down1>}
              <PiCaretRightBold
                size={size(2.175)}
                style={{ marginLeft: 1 }}
              />
            </FloatingButton>
          </div>
        </div>
      }
      {profilesInitialized && 
        <div
          className={scrollTrigger ? 'animation__slide-down-bottom-grid-nav' : 'animation__slide-up-bottom-grid-nav' }
          style={{
            position: 'absolute',
            bottom: appBottombar,
            width: '100%',
            padding: '0 8px 6px 8px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            zIndex: 1
          }}>
          {!isGrid && 
            <FloatingButton
              disabled={showHiddenDisplay}
              onClick={() => navigateToUserOnMap()}
              style={{
                backgroundColor: 'transparent',
                backdropFilter: 'none',
                boxShadow: 'none',
                borderColor: 'transparent',
                marginRight: size(1),
                minWidth: size(5),
                height: size(5),
                marginBottom: size(0.5),
                padding: 0,
              }}>
              <PiCrosshair fill='white' size={size(5)} />
            </FloatingButton>
          }
          <FloatingButton
            disabled={showHiddenDisplay}
            onClick={() => openFilterDialog()}
            style={{
              backgroundColor: showHiddenDisplay && '#333333',
              // backgroundColor: showHiddenDisplay ? '#333333' : (savedState?.isActive && colors.statusColors['right_now']('primary')),
              marginRight: size(1.2),
              marginBottom: size(0.5),
              height: size(5),
            }}>
            <TbAdjustmentsHorizontal
              size={size(2.5)}
              color={savedState?.isActive ? colors.statusColors['right_now']('light') : 'white'}
              style={{
                marginRight: size(0.5),
                transform: 'translateY(-1px)',
              }}
            />
            Filters
          </FloatingButton>
          {/* <PostActionBtn
            onClick={() => newPost.open()}
            style={{ position: 'relative' }}
          /> */}
          <FloatingButton
            onClick={() => newPost.open()}
            style={{
              backgroundColor: 'black',
              minWidth: size(7),
              height: size(7),
              border: 'none',
              padding: 0,
            }}>
            <img
              src={'https://mandrake-app.b-cdn.net/dev/application/icons/plus-icon.svg'}
              alt='create post'
              style={{
                flexShrink: 0,
                height: size(7),
                width: size(7)
              }}
            />
          </FloatingButton>
        </div>
      }
    </div>
  )
}

export default NearbyTopNav
